import { CloseOutlined } from '@ant-design/icons';
import { Button, Icon, Select } from 'antd';
import classNames from 'classnames';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { propertyUnpublishReason as CancelReasons } from '~constants';

function PropertiesBulkOperationModal({ list, selectedProperties, onClose, onSuccess }) {
  const { t } = useTranslation();
  const [selectedReason, setSelectedReason] = useState();
  const [formValidationError, setFormValidationError] = useState({ unpublishReason: null });

  const isMultiSelect = useMemo(() => {
    return selectedProperties.length > 1;
  }, [selectedProperties]);

  const firstProperty = useMemo(() => {
    return list.find(p => p.id === selectedProperties[0]);
  }, [selectedProperties, list]);

  const handleSubmit = useCallback(
    reason => {
      if (reason) {
        onSuccess(reason);
      } else {
        setFormValidationError({
          unpublishReason: t('cms.properties_bulk_operation_confirm.modal.field.unpublish.blank'),
        });
      }
    },
    [t],
  );

  const handleSelectorChange = useCallback(v => {
    setSelectedReason(v);
    setFormValidationError({ unpublishReason: null });
  }, []);

  return (
    <div className="properties-bulk-operation-container">
      <div className="properties-bulk-operation-container__header">
        <CloseOutlined
          className="properties-bulk-operation-container__close-button"
          onClick={onClose}
        />
      </div>
      <div className="properties-bulk-operation-container__body">
        <div className="properties-bulk-operation-container__body__title">
          <Choose>
            <When condition={isMultiSelect}>
              {t('cms.properties_bulk_operation_confirm.modal.title.multi')}
            </When>
            <Otherwise>{t('cms.properties_bulk_operation_confirm.modal.title.single')}</Otherwise>
          </Choose>
        </div>
        <section className="properties-bulk-operation-container__section">
          <div className="properties-bulk-operation-container__bulk-summary">
            <Choose>
              <When condition={isMultiSelect && firstProperty}>
                <div>
                  <Icon type="inbox" />
                  <span className="properties-bulk-operation-container__bulk-summary__emphasis">
                    {t('cms.properties_bulk_operation_confirm.modal.operation.bulk_unpublish')}
                  </span>
                  <span className="properties-bulk-operation-container__bulk-summary__divider">
                    |
                  </span>
                  <span>
                    {t('cms.properties_bulk_operation_confirm.modal.operation.selected_num', {
                      num: selectedProperties.length,
                    })}
                  </span>
                </div>
              </When>
              <Otherwise>
                <div>
                  <div>
                    {`${t('cms.properties_bulk_operation_confirm.modal.field.property')}: ${
                      firstProperty?.name
                    }
                    `}
                  </div>
                  <div>
                    {`${t('cms.properties_bulk_operation_confirm.modal.field.landlord_name')}: ${
                      firstProperty?.landlordName
                    }
                    `}
                  </div>
                  <div>
                    {`${t('cms.properties_bulk_operation_confirm.modal.field.city')}: ${
                      firstProperty?.cityName
                    }`}
                  </div>
                </div>
              </Otherwise>
            </Choose>
          </div>

          <div>
            <div className="properties-bulk-operation-container__form-item__title">
              <span className="properties-bulk-operation-container__form-item__indicator">*</span>
              {t('cms.properties_bulk_operation_confirm.modal.field.unpublish')}:
            </div>
            <Select
              className={classNames('properties-bulk-operation-container__form-item--select', {
                'has-error': formValidationError.unpublishReason,
              })}
              value={selectedReason}
              onChange={handleSelectorChange}
              placeholder={t(
                'cms.properties_bulk_operation_confirm.modal.field.unpublish.placeholder',
              )}
            >
              <For index="index" each="reason" of={Object.keys(CancelReasons)}>
                <Select.Option value={reason} key={index}>
                  {t(
                    `cms.properties_bulk_operation_confirm.modal.field.unpublish.option.${CancelReasons[reason]}`,
                  )}
                </Select.Option>
              </For>
            </Select>
            <If condition={formValidationError.unpublishReason}>
              <div className="properties-bulk-operation-container__form-item__err">
                {formValidationError.unpublishReason}
              </div>
            </If>
          </div>
        </section>
      </div>
      <div className="properties-bulk-operation-container__footer">
        <Button
          size="large"
          className="properties-bulk-operation-container__cancel-button"
          onClick={onClose}
        >
          {t('cms.properties_bulk_operation.action.btn.cancel')}
        </Button>

        <Button
          type="primary"
          size="large"
          className="properties-bulk-operation-container__save-button"
          onClick={() => handleSubmit(selectedReason)}
        >
          {t('cms.properties_bulk_operation_confirm.modal.btn.confirm')}
        </Button>
      </div>
    </div>
  );
}

export default PropertiesBulkOperationModal;
